import { useCallback, useSyncExternalStore } from 'react';
import { Drawer, Stack } from '@mui/material';
import { HEADER_HEIGHT, TestIds } from '../../constants';
import { overlaySlotStore } from '../../services/overlaySlots/overlaySlotStore';

/** Offset drawer paper + backdrop below the fixed app header (matches Notifications). */
const BELOW_HEADER = {
  marginTop: `${HEADER_HEIGHT}px`,
  height: `calc(100% - ${HEADER_HEIGHT}px)`,
} as const;

const DRAWER_WIDTH = {
  xs: '100vw',
  sm: 'clamp(320px, 30vw, 480px)',
} as const;

/**
 * Singleton hovering overlay host. Empty paper only — claimants portal their
 * own chrome into `snapshot.element`. Width, transparent backdrop, below-header
 * offset, and dialog a11y match today's NotificationDrawer defaults.
 */
export function OverlayDrawerHost() {
  const snapshot = useSyncExternalStore(overlaySlotStore.subscribe, overlaySlotStore.getSnapshot);

  const mountRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;
      if (!snapshot.element) {
        node.replaceChildren();
        return;
      }
      if (!node.contains(snapshot.element)) {
        snapshot.element.style.width = '100%';
        snapshot.element.style.height = '100%';
        snapshot.element.style.minHeight = '0';
        snapshot.element.style.overflow = 'auto';
        node.replaceChildren(snapshot.element);
      }
    },
    [snapshot.element]
  );

  return (
    <Drawer
      data-testid={TestIds.OVERLAY_DRAWER_HOST}
      anchor="right"
      variant="temporary"
      open={snapshot.open}
      onClose={() => overlaySlotStore.close()}
      ModalProps={{
        keepMounted: true,
      }}
      slotProps={{
        paper: {
          // Stable id so overlay claimants (e.g. the notification bell) can point
          // aria-controls at the shared dialog surface (WCAG 4.1.2, PSS-2755).
          id: 'psp-overlay-drawer',
          role: 'dialog',
          'aria-modal': true,
          ...(snapshot.ariaLabel ? { 'aria-label': snapshot.ariaLabel } : {}),
        },
      }}
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: 'background.paper',
          borderLeft: 1,
          borderColor: 'divider',
          ...BELOW_HEADER,
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          overflow: 'hidden',
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'transparent',
          ...BELOW_HEADER,
        },
      }}
    >
      <Stack
        ref={mountRef}
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          height: '100%',
        }}
      />
    </Drawer>
  );
}
